"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { dialogFormInitialFocus } from "@/components/ui/dialog-form"; // custom: Enter-confirms dialogs
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js"; // custom: localized

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

// custom: outline Cancel for dialog footers, the one Cancel style app-wide
function DialogCancel({ children, ...props }: DialogPrimitive.Close.Props) {
  const label = children ?? m.common_cancel(); // custom:
  return (
    // custom:
    <DialogPrimitive.Close
      data-slot="dialog-cancel" // custom:
      render={<Button variant="outline" />} // custom:
      {...props} // custom:
    >
      {label /* custom: */}
    </DialogPrimitive.Close>
  ); // custom:
} // custom:

function DialogOverlay({
  className,
  onClick, // custom: composed below so callers can still pass onClick
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs",
        className,
      )}
      onClick={(event) => {
        // custom: same portal-vs-React-tree trap as the popup below, and the
        // custom: one a user hits by habit: clicking the backdrop to dismiss a
        // custom: dialog that sits inside a clickable ancestor (a deck tile
        // custom: <Link>) dismissed it *and* opened that deck.
        event.stopPropagation(); // custom:
        onClick?.(event); // custom:
      }}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onClick, // custom: composed below so callers can still pass onClick
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
}) {
  // custom: in button-only dialogs, land initial focus on the DialogForm's
  // custom: type="submit" primary so Enter confirms right after opening.
  const popupRef = React.useRef<HTMLDivElement | null>(null); // custom:
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        ref={popupRef} // custom: Enter-confirms dialogs
        initialFocus={() => dialogFormInitialFocus(popupRef.current)} // custom: Enter-confirms dialogs; inline closure so the compiler does not see a ref passed to a call in render
        className={cn(
          // custom: max-h + overflow-y-auto + overscroll-contain so tall dialogs scroll instead of overflowing the viewport
          // custom: rounded-lg (scaffold ships rounded-xl) — app-wide surface radius matches form controls
          // custom: ring-border edge and shadow-lg — modal elevation tier (scaffold ships a border and no shadow)
          "bg-popover text-popover-foreground ring-border data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 fixed top-1/2 left-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto overscroll-contain rounded-lg p-4 text-sm shadow-lg ring-1 duration-100 outline-none sm:max-w-sm",
          className,
        )}
        onClick={(event) => {
          // custom: BaseUI portals the popup to the body, but React synthetic events
          // custom: still bubble through the React tree to ancestors. Without this, a
          // custom: dialog rendered inside a clickable ancestor (e.g. a deck tile <Link>)
          // custom: fires that ancestor when a control inside the dialog is clicked —
          // custom: confirming a deck delete navigated into the editor (#159).
          event.stopPropagation(); // custom:
          onClick?.(event); // custom:
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">{m.common_close() /* custom: localized */}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    // custom: gap-1 — title-to-description sits on the 4px tier
    <div data-slot="dialog-header" className={cn("flex flex-col gap-1", className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        // custom: plain action row — DialogContent's gap-4 separates it (scaffold ships a bleeding bordered band)
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          {m.common_close() /* custom: localized */}
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-heading text-base leading-none font-medium", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-sm *:[a]:underline *:[a]:underline-offset-3",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogCancel, // custom: outline Cancel
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
